/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode, useEffect, useMemo } from 'react';
import {
  CardBase,
  FlexContainer,
  GridContainer,
  Label,
  useDeviceSize,
  useTimezone,
} from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useData, useOperation } from '../../providers';
import {
  Asset,
  DOCUMENT_BACK,
  DOCUMENT_FRONT,
  DOCUMENT_SELFIE,
  IconInfo,
  RoutesName,
  SseEvents,
  SseResultObject,
  circleIconData,
} from '../../states';
import { previousOnboardingsValidationState } from '../../states/atoms';
import { CardListHeader } from '../cards';
import { CardList, DataList } from '../cards/CardList';
import { GoBackHeader } from '../goBackHeader';
import { EmptyFilesError, EmptyFilesUpload } from '../icons';
import { Banner } from './Banner';
import { ImageLoad } from './ImageLoad';
import {
  FacialContainer,
  FacialCheckGrid,
  ResultsDataImg,
  ResultsDataImgWrapper,
} from './Styles';

type PreviousOperation = Partial<
  SseResultObject[SseEvents.PREVIOUS_ONBOARDING_VALIDATED]
> & {
  assets: Asset[];
};

export const PreviousOnboardings = () => {
  const { t } = useTranslation();
  const { operationId } = useParams();
  const { isMobile } = useDeviceSize();
  const navigate = useNavigate();
  const { previousOnboardingState } = useData();
  const { opreationAssets } = useOperation();
  const { formatTimezone } = useTimezone();
  const antifraud = useRecoilValue(previousOnboardingsValidationState);

  useEffect(() => {
    if (!antifraud || !operation.found) {
      navigate(-1);
    }
  }, [antifraud]);

  const operation = useMemo<PreviousOperation>(
    () =>
      previousOnboardingState
        ? { ...previousOnboardingState?.data, assets: opreationAssets }
        : { assets: [] },
    [previousOnboardingState, opreationAssets]
  );

  const documents: Asset[] = useMemo(
    () => opreationAssets || [],
    [opreationAssets]
  );

  const dataInfo: DataList[] = [
    {
      title: 'Reason for rejection',
      value: operation.found
        ? t('PREVIOUS ONBOARDING ALREADY REGISTERED')
        : t('NO HAVE'),
      valueStatus: operation.found ? 'error400' : 'success400',
      idText: 'rejection_reason_title_text',
      idValue: 'rejection_reason_description_text',
      ignoreIdType: true,
    },
    // TODO: Add when data are avaialble
    // {
    //   title: 'Digital onboarding date',
    //   value: operation.creationTime
    //     ? formatTimezone(operation.creationTime, 'DD-MM-YYYY')
    //     : formatTimezone(new Date(), 'DD-MM-YYYY'),
    //   idText: 'date_title_text',
    //   idValue: 'date_text',
    //   ignoreIdType: true,
    // },
  ];

  const cardListIcon: IconInfo | undefined = useMemo(
    () =>
      operation.found ? circleIconData['error'] : circleIconData['success'],
    [operation]
  );

  const CardImages = ({
    title,
    children,
    rowGapDesktop = '2.4',
    idTitle,
  }: {
    title: string;
    children: ReactNode;
    rowGapDesktop?: string;
    idTitle?: string;
  }) => {
    return (
      <CardBase>
        <CardListHeader
          flexDirection="column"
          rowGap={isMobile ? '1.6' : rowGapDesktop}
          flex="1"
        >
          <Label fontSize="12" semibold upperCase id={idTitle} testId={idTitle}>
            {t(title)}
          </Label>
          <FlexContainer
            as="section"
            alignItems="center"
            columnGap="3.2"
            rowGap="3.2"
            flexDirection={isMobile ? 'column' : 'row'}
            justifyContent="center"
          >
            {children}
          </FlexContainer>
        </CardListHeader>
      </CardBase>
    );
  };

  const getDocuments = (id: string) =>
    operation.assets
      .filter(
        (element) =>
          element.type === DOCUMENT_FRONT || element.type === DOCUMENT_BACK
      )
      .reverse()
      .map(({ type, url }, index) => (
        <ResultsDataImg
          key={index}
          prevOnboardingImg={!previousOnboardingState?.data?.found}
          bigImg={previousOnboardingState?.data?.found}
          double={previousOnboardingState?.data?.found}
          id={`${id}_${
            type === DOCUMENT_BACK ? 'document_back' : 'document_front'
          }_image`}
          data-test={`${id}_${
            type === DOCUMENT_BACK ? 'document_back' : 'document_front'
          }_image`}
        >
          <img
            src={url}
            data-test={`${id}_${
              type === DOCUMENT_BACK ? 'document_back' : 'document_front'
            }_image`}
          />
          <Label
            fontSize="12"
            id={`${id}_${
              type === DOCUMENT_BACK ? 'document_back' : 'document_front'
            }_image_description_text`}
            testId={`${id}_${
              type === DOCUMENT_BACK ? 'document_back' : 'document_front'
            }_image_description_text`}
          >
            {t(type)}
          </Label>
        </ResultsDataImg>
      ));

  const archivedImage = useMemo(
    () =>
      operation.assets.find(
        ({ type }: { type: string }) => type.toUpperCase() === DOCUMENT_SELFIE
      ) || undefined,
    [operation]
  );

  const selfieImage = useMemo(
    () =>
      opreationAssets.length
        ? opreationAssets.find(({ type }) => type === DOCUMENT_SELFIE)
        : undefined,
    [operation.assets]
  );

  const getFaceImages = () => (
    <>
      <FacialContainer>
        {selfieImage ? (
          <ImageLoad
            asset={selfieImage}
            testId="previous_onboarding_detail_captured_image"
          />
        ) : (
          <EmptyFilesError />
        )}
      </FacialContainer>

      <FacialContainer>
        {/* 
         TODO: Add when real data are availabe
        {archivedImage ? (
          <img
            src={archivedImage.url}
            alt={archivedImage.type}
            data-test="previous_onboarding_detail_actual_image"
          />
        ) : ( */}
        <EmptyFilesUpload />
      </FacialContainer>
    </>
  );

  const getSelfies = () => (
    <FacialCheckGrid
      alignItems="center"
      columnGap={isMobile ? '0' : '1.4'}
      justifyContent="space-between"
      gridTemplateColumns={isMobile ? '15rem 0rem' : '16rem 0rem'}
      rowGap="0.6"
    >
      {getFaceImages()}
      <>
        <Label
          className="asset-text selfie-text"
          fontSize="12"
          textAlign={isMobile ? 'left' : 'center'}
          testId="previous_onboarding_detail_captured_image_description_text"
          id="previous_onboarding_detail_captured_image_description_text"
        >
          {t('Image captured')}
        </Label>
        <Label
          className="asset-text document-text"
          fontSize="12"
          textAlign={isMobile ? 'left' : 'center'}
          testId="previous_onboarding_detail_actual_image_description_text"
          id="previous_onboarding_detail_actual_image_description_text"
        >
          {t('Archived image')}
        </Label>
      </>
    </FacialCheckGrid>
  );

  return (
    <>
      <GoBackHeader
        route={generatePath(RoutesName.results, {
          operationId,
        })}
        linkLabel="Go back"
        title="Previous onboarding"
        linkId="previous_onboarding_detail_back_button"
        idTitle="previous_onboarding_detail_title_text"
        templateColumns="1fr 2fr 1fr"
      />
      <Banner idDocument="previous_onboarding_detail_document_type_text" />
      <GridContainer
        gridTemplateColumns={!operation.found && !isMobile ? '1fr 1fr' : '1fr'}
        gridTemplateRows="auto"
        gap="1.6"
        as="section"
      >
        <CardList
          title={t('ANTI-FRAUD CHECK')}
          subTitle={t('Results') as string}
          data={dataInfo}
          icon={cardListIcon}
          columnsPerRow="1"
          showEmptyState
          cardId="previous_onboarding_detail"
          idTitle="previous_onboarding_detail_section_title_text"
          idSubtitle="previous_onboarding_detail_section_description_text"
          iconResultId="previous_onboarding_detail_checker_image"
        />
        {operation.found && (
          <CardImages
            title="IMAGES CONFRONTED"
            idTitle="previous_onboarding_detail_images_title_text"
          >
            {getSelfies()}
          </CardImages>
        )}
        {documents.length > 0 && (
          <CardImages
            title="Capture document"
            idTitle="previous_onboarding_detail_captured_document_title_text"
          >
            <ResultsDataImgWrapper
              wrap={isMobile ? 'no-wrap' : 'wrap'}
              justifyContent="center"
              alignItems="flex-start"
              testId="images-obtained"
            >
              {getDocuments('previous_onboarding_detail_captured')}
            </ResultsDataImgWrapper>
          </CardImages>
        )}
        {/* 
          TODO: Adapt when all needed data are available
        {operation.found && documents.length && (
          <CardImages
            title="ORIGINAL DOCUMENT"
            idTitle="previous_onboarding_detail_actual_document_title_text"
          >
            <ResultsDataImgWrapper
              wrap={isMobile ? 'no-wrap' : 'wrap'}
              justifyContent="flex-start"
              alignItems="flex-start"
              testId="images-obtained"
            >
              {getDocuments('previous_onboarding_detail_actual')}
            </ResultsDataImgWrapper>
          </CardImages>
        )} */}
      </GridContainer>
    </>
  );
};
